import { type FC, useCallback, useRef, useState } from "react";

import { useMutation } from "@apollo/client";

import { useBlizzardEvents } from "../../analytics/hooks";
import { ExperienceType } from "../../gql-generated/types";
import {
    type SetExperienceDismissedMutation,
    type SetExperienceDismissedMutationVariables,
    setExperiencedDismissed,
} from "../api/mutations";
import { useFeedbackRatingShowService } from "../hooks";

import { type FeedbackRating } from "./shared.types";

interface FeedbackRatingModelProvider {
    FeedbackRatingComponent: FC<FeedbackRating>;
}

export const FeedbackRatingModelProvider: FC<FeedbackRatingModelProvider> = ({ FeedbackRatingComponent }) => {
    const [setFeedbackRatingDismissed] = useMutation<
        SetExperienceDismissedMutation,
        SetExperienceDismissedMutationVariables
    >(setExperiencedDismissed, { variables: { input: { experienceType: ExperienceType.FeedbackRating } } });
    const [rating, setRating] = useState(0);
    const [commentText, setCommentText] = useState("");
    const { show, setShow } = useFeedbackRatingShowService();
    const { sendBlizzardEvent } = useBlizzardEvents();
    const isInitialCommentRef = useRef(true);

    const handleClose = useCallback(() => {
        sendBlizzardEvent({
            event: "MYLENSES_RATING_FORM_ACTION",
            action: "DISMISS_FORM",
        });
        setShow(false);
        void setFeedbackRatingDismissed();
    }, [sendBlizzardEvent, setShow, setFeedbackRatingDismissed]);

    const handleRatingChange = useCallback(
        (newRating: number) => {
            sendBlizzardEvent({
                event: "MYLENSES_RATING_FORM_ACTION",
                action: "SELECT_RATING",
                rating: newRating,
            });
            setRating(newRating);
        },
        [sendBlizzardEvent, setRating],
    );

    const handleCommentTextChange = useCallback(
        (newCommentText: string) => {
            if (isInitialCommentRef.current) {
                sendBlizzardEvent({
                    event: "MYLENSES_RATING_FORM_ACTION",
                    action: "ENTER_COMMENT",
                });
                isInitialCommentRef.current = false;
            }
            setCommentText(newCommentText);
        },
        [setCommentText, sendBlizzardEvent],
    );

    const onSubmit = useCallback(() => {
        sendBlizzardEvent({
            event: "MYLENSES_RATING_FORM_ACTION",
            action: "SUBMIT",
            comment: commentText,
            rating,
        });
        setShow(false);
        void setFeedbackRatingDismissed();
    }, [sendBlizzardEvent, setShow, setFeedbackRatingDismissed, commentText, rating]);

    if (!show) {
        return null;
    }

    return (
        <FeedbackRatingComponent
            rating={rating}
            onClose={handleClose}
            commentText={commentText}
            onSubmit={onSubmit}
            onRatingChange={handleRatingChange}
            onCommentTextChange={handleCommentTextChange}
        />
    );
};
